import React, { useState, useEffect,useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { ArrowUpCircle, ArrowDownCircle, DollarSign, Scale, AlertCircle, CheckCircle } from 'lucide-react';

export default function Trade() {
  const codeInputRef = useRef([]);
  const [isHolidayToday, setIsHolidayToday] = useState(false);
const [isCheckingHoliday, setIsCheckingHoliday] = useState(true);
  const [priceData, setPriceData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [balances, setBalances] = useState({ balance_rial: 0, balance_gold: 0 });
  const [amountRial, setAmountRial] = useState('');
  const [amountGold, setAmountGold] = useState('');
  const [activeTab, setActiveTab] = useState('buy');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const [pendingGoldRequests, setPendingGoldRequests] = useState([]);
  const [pendingWithdrawRequests, setPendingWithdrawRequests] = useState([]);

  // --- توابع کمکی ---
  const vibrate = (pattern = 50) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const numberToPersianWords = (number) => {
    const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
    const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
    const tens = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
    const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
    const thousands = ['هزار', 'میلیون', 'میلیارد'];

    if (number === 0) return 'صفر';

    const toWords = (num) => {
      if (num < 10) return units[num];
      if (num < 20) return teens[num - 10];
      if (num < 100) {
        const ten = Math.floor(num / 10);
        const unit = num % 10;
        return unit === 0 ? tens[ten] : `${tens[ten]} و ${units[unit]}`;
      }
      if (num < 1000) {
        const hundred = Math.floor(num / 100);
        const rest = num % 100;
        return rest === 0 ? hundreds[hundred] : `${hundreds[hundred]} و ${toWords(rest)}`;
      }
      return num.toString();
    };

    let result = '';
    let num = Math.floor(number);
    let level = 0;

    while (num > 0) {
      const part = num % 1000;
      if (part > 0) {
        const partWords = toWords(part);
        const thousand = level > 0 ? ` ${thousands[level - 1]}` : '';
        result = `${partWords}${thousand}${result ? ' و ' + result : ''}`;
      }
      num = Math.floor(num / 1000);
      level++;
    }

    return result || 'صفر';
  };

  const convertToSootWords = (goldAmount) => {
    if (!goldAmount || isNaN(goldAmount)) return '';
    const kilograms = Math.floor(goldAmount / 1000);
    const grams = Math.floor(goldAmount % 1000);
    const soot = Math.round((goldAmount - Math.floor(goldAmount)) * 1000);
    let result = '';

    if (kilograms > 0) result += numberToPersianWords(kilograms) + ' کیلوگرم';
    if (grams > 0) result += (result ? ' و ' : '') + numberToPersianWords(grams) + ' گرم';
    if (soot > 0) result += (result ? ' و ' : '') + numberToPersianWords(soot) + ' سوت';
    return result || 'صفر';
  };

  const persianToEnglishDigits = (str) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = str.toString();
    for (let i = 0; i < persianDigits.length; i++) {
      result = result.replace(new RegExp(persianDigits[i], 'g'), englishDigits[i]);
    }
    return result;
  };

  // --- فرمت اعداد (امن) ---
  const formatNumber = (value) => {
    if (value == null || isNaN(parseFloat(value))) return '';
    const num = parseFloat(value);
    return num.toLocaleString('fa-IR');
  };

  const formatGoldNumber = (value) => {
    if (value == null || isNaN(parseFloat(value))) return '';
    const num = parseFloat(value);
    if (num === 0) return '0.000';
    return num.toLocaleString('fa-IR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  // --- محاسبات ---
  const calculatedGoldFromRial = amountRial && priceData?.buy_price && priceData?.sell_price ? (
    activeTab === 'buy'
      ? parseFloat(amountRial) / priceData.buy_price 
      : parseFloat(amountRial) / priceData.sell_price
  ) : 0;

  const calculatedRialFromGold = amountGold && priceData?.buy_price && priceData?.sell_price ? (
    activeTab === 'buy'
      ? parseFloat(amountGold) * priceData.buy_price
      : parseFloat(amountGold) * priceData.sell_price
  ) : 0;

  // --- هندلرها ---
  const handleAmountRialChange = (e) => {
    let raw = e.target.value;
    raw = persianToEnglishDigits(raw);
    raw = raw.replace(/[^\d]/g, '');
    setAmountRial(raw);
    setAmountGold('');
  };

  const handleAmountGoldChange = (e) => {
    let raw = e.target.value;
    raw = persianToEnglishDigits(raw);
    raw = raw.replace(/[^\d.]/g, '');
    const parts = raw.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 6) return;
    setAmountGold(raw);
    setAmountRial('');
  };

  // --- توابع API ---
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchData = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      const [pricesRes, balancesRes, goldRequestsRes, withdrawRequestsRes] = await Promise.all([
        api.get('/api/prices/my-prices', { headers: { Authorization: `Bearer ${newToken}` } }),
        api.get('/api/wallet/balances', { headers: { Authorization: `Bearer ${newToken}` } }),
        api.get('/api/gold/user-requests', { headers: { Authorization: `Bearer ${newToken}` } }),
        api.get('/api/wallet/requests/pending', { headers: { Authorization: `Bearer ${newToken}` } })
      ]);

      // دیباگ: ببین چی دریافت می‌شه
      console.log('قیمت‌های دریافتی:', pricesRes.data);

      setPriceData(pricesRes.data);

      setBalances({
        balance_rial: parseFloat(balancesRes.data.balance_rial || 0),
        balance_gold: parseFloat(balancesRes.data.balance_gold || 0)
      });

      const goldRequests = Array.isArray(goldRequestsRes.data) ? goldRequestsRes.data : [];
      setPendingGoldRequests(goldRequests.filter(req => req?.status === 'pending'));

      const withdrawRequests = Array.isArray(withdrawRequestsRes.data) ? withdrawRequestsRes.data : [];
      setPendingWithdrawRequests(withdrawRequests);

    } catch (err) {
      console.error('خطا در دریافت اطلاعات:', err);
      setError(err.response?.data?.message || 'خطا در دریافت اطلاعات');
      setTimeout(() => setError(''), 3000);
    }
  };

// داخل useEffect اصلی (همراه fetchData)
useEffect(() => {
  if (!token) {
    navigate('/login_page');
    return;
  }

  const loadData = async () => {
    setIsCheckingHoliday(true);
    try {
      await Promise.all([
        fetchData(),
        checkHolidayStatus()
      ]);
    } finally {
      setIsCheckingHoliday(false);
    }
  };

  loadData();
  const interval = setInterval(fetchData, 10000);
  return () => clearInterval(interval);
}, [token, navigate]);

// تابع جدید: بررسی وضعیت تعطیلات
const checkHolidayStatus = async () => {
  try {
    const res = await api.get('/api/prices/holiday-status');
    setIsHolidayToday(res.data.is_holiday);
  } catch (err) {
    console.warn('خطا در دریافت وضعیت تعطیلات:', err);
    setIsHolidayToday(false);
  }
};

  useEffect(() => {
    let interval;
    if (isCodeSent && timer > 0) {
      interval = setInterval(() => setTimer(prev => prev - 1), 1000);
    } else if (timer <= 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [isCodeSent, timer]);

  const showMessage = (msg, type) => {
    if (type === 'success') setSuccess(msg);
    else setError(msg);
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    vibrate(50);

    if (!amountRial && !amountGold) return showMessage('لطفاً مقدار را وارد کنید', 'error');
    if (!priceData?.is_price_fresh) return showMessage('قیمت‌ها قدیمی هستند', 'error');

    setIsLoading(true);

    try {
      const newToken = token || (await refreshAccessToken());
      const payload = amountRial ? { amount_rial: parseFloat(amountRial) } : { amount_gold: parseFloat(amountGold) };
      const endpoint = activeTab === 'buy' ? '/api/prices/buy' : '/api/prices/sell';
      const res = await api.post(endpoint, payload, { headers: { Authorization: `Bearer ${newToken}` } });

      showMessage(res.data.message, 'success');
      setIsCodeSent(true);
      setTimer(60);
      setCanResend(false);
      vibrate([100, 50, 100]);
    } catch (err) {
      vibrate(200);
      const msg = err.response?.data?.message || 'خطا در ارسال درخواست';
    
      if (msg.includes('تعطیل رسمی')) {
        setError('امروز تعطیل رسمی است. امکان تراکنش وجود ندارد.');
      } else if (msg.includes('قیمت‌ها قدیمی')) {
        setError('قیمت‌ها به‌روز نیستند. لطفاً چند لحظه دیگر تلاش کنید.');
      } else {
        showMessage(msg, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      const payload = amountRial ? { amount_rial: parseFloat(amountRial) } : { amount_gold: parseFloat(amountGold) };
      const endpoint = activeTab === 'buy' ? '/api/prices/buy' : '/api/prices/sell';
      await api.post(endpoint, payload, { headers: { Authorization: `Bearer ${newToken}` } });
      setCode(['', '', '', '', '']);
      setTimer(60);
      setCanResend(false);
      showMessage('کد جدید ارسال شد', 'success');
    } catch (err) {
      showMessage('خطا در ارسال مجدد', 'error');
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    vibrate(50);
    const verificationCode = code.join('');
    setIsLoading(true);

    try {
      const newToken = token || (await refreshAccessToken());
      await api.post('/api/prices/verify-transaction', {
        verification_code: verificationCode,
        transaction_type: activeTab
      }, { headers: { Authorization: `Bearer ${newToken}` } });

      vibrate([100, 50, 100]);
      showMessage('تراکنش موفق!', 'success');
      setAmountRial(''); setAmountGold(''); setIsCodeSent(false); setCode(['', '', '', '', '']);
      fetchData();
    } catch (err) {
      vibrate(200);
      showMessage('کد اشتباه است', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e, index) => {
    const value = e.target.value.slice(0, 1);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 4) {
      document.getElementById(`code-${index + 1}`).focus();
    }
  };

  // --- Web OTP Auto-fill ---
useEffect(() => {
  if (!isCodeSent) return;
  if (!('OTPCredential' in window)) return;

  const ac = new AbortController();

  navigator.credentials
    .get({
      otp: { transport: ['sms'] },
      signal: ac.signal,
    })
    .then((otp) => {
      if (otp && otp.code && otp.code.length === 5) {
        const digits = otp.code.split('');
        setCode(digits);

        // فوکوس روی آخرین فیلد
        setTimeout(() => {
          codeInputRef.current[4]?.focus();
        }, 100);

        // خودکار تأیید (اختیاری)
        setTimeout(() => {
          const fakeEvent = { preventDefault: () => {} };
          handleVerifyCode(fakeEvent);
        }, 600);
      }
    })
    .catch((err) => {
      console.log('Web OTP cancelled or failed:', err);
    });

  return () => ac.abort();
}, [isCodeSent]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir relative">
      <div className="max-w-5xl mx-auto">

        {/* پیام‌ها */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <AnimatePresence>
            {error && (
              <motion.div initial={{ y: -100 }} animate={{ y: 0 }} exit={{ y: -100 }} transition={{ type: "spring" }}>
                <div className="bg-red-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-red-300 flex items-center gap-3">
                  <AlertCircle className="w-6 h-6" />
                  <span className="font-medium">{error}</span>
                </div>
              </motion.div>
            )}
            {success && (
              <motion.div initial={{ y: -100 }} animate={{ y: 0 }} exit={{ y: -100 }} transition={{ type: "spring" }}>
                <div className="bg-green-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-green-300 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-medium">{success}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* عنوان */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-10"
        >
          خرید و فروش طلا
        </motion.h2>

        {/* موجودی */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 shadow-xl border border-white/50">
            <p className="text-gray-600 text-sm mb-2">موجودی ریالی</p>
            <p className="text-3xl font-bold text-indigo-700">
              {formatNumber(balances.balance_rial)} ریال
            </p>
            <p className="text-sm text-indigo-600 mt-1">
              ≈ {Math.floor(balances.balance_rial / 10).toLocaleString('fa-IR')} تومان
            </p>
          </div>
          <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-3xl p-6 shadow-xl border border-amber-200">
            <p className="text-gray-700 text-sm mb-2">موجودی طلایی</p>
            <p className="text-3xl font-bold text-amber-800">
              {formatGoldNumber(balances.balance_gold)} گرم
            </p>
          </div>
        </motion.div>

        {/* وضعیت قیمت */}
        {priceData ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-2xl border flex items-center justify-between"
            style={{
              background: priceData.is_price_fresh 
                ? 'linear-gradient(to right, #ecfdf5, #d1fae5)' 
                : 'linear-gradient(to right, #fef3c7, #fde68a)',
              borderColor: priceData.is_price_fresh ? '#10b981' : '#f59e0b'
            }}
          >
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full animate-pulse ${priceData.is_price_fresh ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              <span className={`font-bold ${priceData.is_price_fresh ? 'text-emerald-700' : 'text-amber-700'}`}>
                {priceData.is_price_fresh ? 'قیمت‌ها به‌روز هستند' : 'قیمت‌ها قدیمی هستند'}
              </span>
            </div>
            <span className="text-xs text-gray-600">
            {priceData.last_updated ? 
  (() => {
    const d = new Date(priceData.last_updated);
    const time = d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
    const date = d.toLocaleDateString('fa-IR', { year: 'numeric', month: 'short', day: 'numeric' });
    return `${time}  ---  ${date}`;
  })()
  : 'نامشخص'
}

            </span>
          </motion.div>
        ) : null}
{isHolidayToday && (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-6 p-5 bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-300 rounded-2xl text-orange-800 text-center font-bold flex items-center justify-center gap-3 shadow-lg"
  >
    <AlertCircle className="w-6 h-6 animate-pulse" />
    <span>امروز تعطیل رسمی است. خرید و فروش طلا امکان‌پذیر نیست.</span>
  </motion.div>
)}
        {/* قیمت‌ها */}
        {priceData ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-3xl p-6 shadow-xl border border-green-200 text-center">
              <p className="text-lg font-bold text-green-700 mb-2">قیمت خرید</p>
              <p className="text-3xl font-bold text-green-800">
                {priceData.buy_price != null && priceData.buy_price > 0 
                  ? formatNumber(priceData.buy_price) 
                  : 'در حال بارگذاری...'}
              </p>
            </div>
            <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-3xl p-6 shadow-xl border border-red-200 text-center">
              <p className="text-lg font-bold text-red-700 mb-2">قیمت فروش</p>
              <p className="text-3xl font-bold text-red-800">
                {priceData.sell_price != null && priceData.sell_price > 0 
                  ? formatNumber(priceData.sell_price) 
                  : 'در حال بارگذاری...'}
              </p>
            </div>
          </motion.div>
        ) : (
          <div className="text-center py-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"
            />
            <p className="text-gray-600 mt-4">در حال دریافت قیمت‌ها...</p>
          </div>
        )}

        {/* فرم اصلی */}
        <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl p-6 md:p-10 border border-gray-100">
          <div className="flex justify-center mb-8">
            <div className="flex bg-gray-100 p-1.5 rounded-2xl shadow-inner">
              {[
                { id: 'buy', label: 'خرید طلا', icon: ArrowUpCircle, color: 'from-green-500 to-emerald-600' },
                { id: 'sell', label: 'فروش طلا', icon: ArrowDownCircle, color: 'from-red-500 to-rose-600' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="relative flex-1 px-6 py-4 rounded-xl font-bold text-sm md:text-lg flex items-center gap-2 md:gap-3 justify-center overflow-hidden transition-all duration-300"
                >
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="trade-tab-indicator"
                      className={`absolute inset-0 bg-gradient-to-r ${tab.color} rounded-xl shadow-lg pointer-events-none z-0`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                    />
                  )}
                  <div className="flex items-center gap-2 md:gap-3 z-10 relative">
                    <tab.icon className={`w-5 h-5 md:w-6 md:h-6 ${activeTab === tab.id ? 'text-white' : 'text-gray-600'}`} />
                    <span className={`whitespace-nowrap ${activeTab === tab.id ? 'text-white' : 'text-gray-700'}`}>
                      {tab.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {!isCodeSent && (
            <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit}
            className="space-y-8"
            style={{ pointerEvents: isHolidayToday ? 'none' : 'auto', opacity: isHolidayToday ? 0.6 : 1 }}
          >
              {/* مقدار ریالی */}
              <div>
                <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <DollarSign className="w-6 h-6 text-indigo-600" />
                  مقدار ریالی (ریال)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amountRial ? formatNumber(amountRial) : ''}
                  onChange={handleAmountRialChange}
                  className="w-full px-6 py-5 text-xl font-mono text-left border-2 border-gray-300 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all"
                  placeholder="5,000,000"
                  dir="ltr"
                  disabled={!priceData?.is_price_fresh}
                />
                {amountRial && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-2 text-left">
                    <p className="text-base font-bold text-indigo-600">
                      {numberToPersianWords(Math.floor(amountRial / 10))} تومان
                    </p>
                    {priceData && (
                      <p className="text-sm text-gray-600 mt-1">
                        معادل: {formatGoldNumber(calculatedGoldFromRial)} گرم طلا
                      </p>
                    )}
                  </motion.div>
                )}
              </div>

              <div className="flex items-center justify-center my-4">
                <div className="h-px w-full bg-gray-300"></div>
                <span className="px-4 text-sm font-bold text-gray-500 bg-white">یا</span>
                <div className="h-px w-full bg-gray-300"></div>
              </div>

              {/* مقدار وزنی */}
              <div>
                <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Scale className={`w-6 h-6 ${activeTab === 'buy' ? 'text-green-600' : 'text-red-600'}`} />
                  مقدار وزنی (گرم)
                </label>
                <input
                  type="tel"
                  inputMode="decimal"
                  step="any"
                  value={amountGold}
                  onChange={handleAmountGoldChange}
                  className="w-full px-6 py-5 text-xl font-mono text-left border-2 border-gray-300 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all"
                  placeholder="2.500"
                  dir="ltr"
                  disabled={!priceData?.is_price_fresh}
                />
                {amountGold && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-2 text-left">
                    <p className="text-base font-bold text-indigo-600">
                      {convertToSootWords(amountGold)}
                    </p>
                    {priceData && (
                      <p className="text-sm text-gray-600 mt-1">
                        معادل: {formatNumber(calculatedRialFromGold)} ریال
                      </p>
                    )}
                  </motion.div>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || (!amountRial && !amountGold) || !priceData?.is_price_fresh ||
                  isHolidayToday}
                className={`w-full py-5 rounded-2xl font-bold text-xl text-white shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all disabled:opacity-50 flex items-center justify-center gap-3 ${
                  activeTab === 'buy'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                    : 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700'
                }`}
              >
                {isLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-6 h-6 border-4 border-white border-t-transparent rounded-full"
                    />
                    <span>در حال ارسال...</span>
                  </>
                ) : (
                  <>
                    {activeTab === 'buy' ? <ArrowDownCircle className="w-7 h-7" /> : <ArrowUpCircle className="w-7 h-7" />}
                    {activeTab === 'buy' ? 'درخواست خرید طلا' : 'درخواست فروش طلا'}
                  </>
                )}
              </button>
            </motion.form>
          )}
        </div>

        {/* Sheet تأیید کد */}
        <AnimatePresence>
          {isCodeSent && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => { setIsCodeSent(false); setCode(['', '', '', '', '']); setTimer(60); setCanResend(false); }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              />
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                className="fixed left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 overflow-y-auto pb-8"
                style={{ bottom: '0', maxHeight: '90vh' }}
              >
                <div className="p-6 pb-safe">
                  <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                    تأیید {activeTab === 'buy' ? 'خرید' : 'فروش'} طلا
                  </h3>
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-5 mb-6 border border-indigo-200">
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">نوع:</span>
                        <span className="font-medium">{activeTab === 'buy' ? 'خرید' : 'فروش'}</span>
                      </div>
                      {amountRial && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">مقدار ریالی:</span>
                          <span className="font-mono font-bold">{formatNumber(amountRial)} ریال</span>
                        </div>
                      )}
                      {amountGold && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">مقدار طلا:</span>
                          <span className="font-mono font-bold">{formatGoldNumber(amountGold)} گرم</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <form onSubmit={handleVerifyCode} className="space-y-6">
                    <p className="text-center text-lg font-medium text-gray-700">کد تأیید را وارد کنید</p>
                    <div className="flex justify-center gap-3" dir="ltr">
                    {code.map((digit, i) => (
  <input
    key={i}
    id={`code-${i}`}
    ref={(el) => (codeInputRef.current[i] = el)}  // اضافه شد
    type="tel"
    inputMode="numeric"
    value={digit}
    onChange={(e) => handleCodeChange(e, i)}
    className="w-16 h-16 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all shadow-sm"
    maxLength={1}
    required
  />
))}
                    </div>
                    <div className="text-center">
                      {!canResend ? (
                        <p className="text-sm text-gray-500">{timer} ثانیه تا ارسال مجدد</p>
                      ) : (
                        <button type="button" onClick={handleResendCode} className="text-indigo-600 hover:text-indigo-700 font-bold">
                          ارسال مجدد کد
                        </button>
                      )}
                    </div>
                    <div className="flex gap-3 mt-6">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-70"
                      >
                        {isLoading ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              className="w-5 h-5 border-4 border-white border-t-transparent rounded-full inline-block mr-2"
                            />
                            در حال تأیید...
                          </>
                        ) : (
                          'تأیید و نهایی کردن'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setIsCodeSent(false); setCode(['', '', '', '', '']); setTimer(60); setCanResend(false); }}
                        className="flex-1 bg-gradient-to-r from-red-500 to-rose-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-red-600 hover:to-rose-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
                      >
                        انصراف
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}